import React, { ReactElement } from "react"
import Draggable from "react-draggable"

import CloseIcon from "@mui/icons-material/Close"
import { DialogProps, IconButtonProps, Paper, PaperProps } from "@mui/material"

import { useTranslations } from "@cirrus/core/i18n/provider"

import { StyledActions, StyledCloseButton, StyledContent, StyledDialog, Title } from "./styled"
import { Button, Tooltip } from "../../atoms"

interface IProps extends DialogProps {
  confirmBtnText?: string,
  onConfirm?: () => void,
  confirmBtnDisabled?: boolean,
  cancelBtnText?: string,
  onClose?: () => void,
  actionButtons?: ReactElement,
  children?: ReactElement,
  title?: string,
  hideActions?: boolean,
  disableBackdropClick?: boolean,
  hideTitle?: boolean,
  hideCloseButton?: boolean,
}

enum CloseReason {
  backdropClick = "backdropClick",
  escapeKeyDown = "escapeKeyDown"
}

const DIALOG_TITLE_ID = "dialog-title"

const DraggablePaper = (props: PaperProps) => {
  // @ts-ignore
  return <Draggable
    handle={`#${DIALOG_TITLE_ID}`}
    cancel={"[class*=MuiDialogContent-root]"}>
    <Paper {...props} />
  </Draggable>
}

export const Dialog = ({
                         confirmBtnText,
                         onConfirm,
                         confirmBtnDisabled,
                         cancelBtnText,
                         onClose,
                         actionButtons,
                         children = <></>,
                         title,
                         hideActions,
                         disableBackdropClick = false,
                         hideTitle = false,
                         hideCloseButton = false,
                         draggable = false,
                         ...props
                       }: IProps) => {

  const { i18n } = useTranslations()

  const defaultActionButtons = (
    <>
      <Button
        variant="contained"
        color="primary"
        label={confirmBtnText || i18n("General:Yes")}
        onClick={() => onConfirm?.()}
        disabled={confirmBtnDisabled}
      />

      <Button
        color="secondary"
        variant="outlined"
        label={cancelBtnText || i18n("General:Cancel")}
        onClick={() => onClose?.()} />
    </>
  )

  const onCloseHandler = (_, reason: CloseReason) => {
    if (props.disableEscapeKeyDown && reason === CloseReason.escapeKeyDown) {
      return
    }

    if (disableBackdropClick && reason === CloseReason.escapeKeyDown) {
      return
    }

    onClose?.()
  }

  return <StyledDialog
    PaperComponent={draggable
      ? DraggablePaper
      : Paper}
    onClose={onCloseHandler}
    {...props}>

    <Title sx={{ cursor: draggable ? "move" : "inherit" }}
           id={DIALOG_TITLE_ID}>
      <Tooltip title={(title || "").length > 25 ? title : ""}>
        {title || ""}
      </Tooltip>
    </Title>

    {onClose && !hideCloseButton && <CloseButton onClick={onClose} />}

    <StyledContent>{children}</StyledContent>

    {
      !hideActions
      && <StyledActions>{actionButtons || defaultActionButtons}</StyledActions>
    }

  </StyledDialog>

}

const CloseButton = (props: IconButtonProps) =>
  <StyledCloseButton {...props}>
    <CloseIcon fontSize={"small"} sx={{ color: "rgba(0, 0, 0, 0.87)" }} />
  </StyledCloseButton>
